import React from "react";

/**
 * v78.10.1 — Global error boundary. Without this, any unhandled render
 * exception (a single component throwing on null props, a stale cache
 * shape mismatch, a Capacitor WebView quirk) would blank the entire
 * SPA on production. With it, the rest of the app keeps running and
 * the user sees a recoverable "Something broke — reload" card scoped
 * to wherever the error originated.
 *
 * Mounted once at the App root. The reset button does a hard refresh
 * so a stale JS bundle can re-download cleanly.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Stash for our own debugging — we don't depend on a 3rd-party
        // crash reporter being wired up. If `window.dataLayer` exists
        // (GTM / GA4), forward the event so we can read repro rates.
        this.setState({ info });
        try {
            // eslint-disable-next-line no-console
            console.error("[Adukoo ErrorBoundary]", error, info);
            if (window.dataLayer && Array.isArray(window.dataLayer)) {
                window.dataLayer.push({
                    event: "app_crash",
                    error: String(error?.message || error),
                    stack: String(error?.stack || "").slice(0, 1200),
                });
            }
        } catch (_e) { /* never crash inside the crash handler */ }
    }

    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div
                data-testid="app-error-boundary"
                className="min-h-screen flex items-center justify-center p-6 bg-aduku-bg"
            >
                <div className="max-w-md w-full bg-white border-2 border-aduku-yellow rounded-2xl shadow-lg p-6 text-center">
                    <div className="text-5xl mb-3">⚠️</div>
                    <h1 className="font-display text-xl font-black text-aduku-navy">
                        Something went wrong loading this page
                    </h1>
                    <p className="text-aduku-navy/70 text-sm mt-2 leading-relaxed">
                        It&apos;s likely a temporary glitch — most of the time a clean
                        reload fixes it. If you keep seeing this, please email{" "}
                        <a className="underline" href="mailto:reachus@adukoo.com">
                            reachus@adukoo.com
                        </a>
                        .
                    </p>
                    <div className="mt-5 flex gap-2 justify-center flex-wrap">
                        <button
                            data-testid="app-error-reload-btn"
                            onClick={() => {
                                try { sessionStorage.removeItem("aduku_token"); } catch (_e) { /* noop */ }
                                // Hard reload bypasses cache, redownloads the bundle.
                                window.location.reload();
                            }}
                            className="h-11 px-5 rounded-full bg-aduku-navy text-aduku-yellow font-bold text-sm hover:bg-aduku-navy-700 active:scale-95 transition-all"
                        >
                            Reload Adukoo
                        </button>
                        <button
                            data-testid="app-error-home-btn"
                            onClick={() => { window.location.href = "/"; }}
                            className="h-11 px-5 rounded-full bg-white border-2 border-aduku-navy text-aduku-navy font-bold text-sm hover:bg-aduku-navy/5 active:scale-95 transition-all"
                        >
                            Go to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
